import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { toast } from './Toast';
import {
  runNationalFeedUpdate, generateEvergreenTopics, forceTopicOfDayCycle, purgeNewsFeed,
  approveGlobalTopic, rejectGlobalTopic, broadcastUpdateNotification,
  getOpinionInsights, forceAggregateInsights,
  getModerationCases, resolveModerationCase,
  createDataApiKey, listDataApiKeys, revokeDataApiKey,
  getIngestionStatus
} from '../api';
import {
  XMarkIcon, BoltIcon, NewspaperIcon, SparklesIcon, StarIcon,
  CheckIcon, TrashIcon, ArrowPathIcon, FireIcon, GlobeAmericasIcon,
  ExclamationTriangleIcon, BellAlertIcon
} from '@heroicons/react/24/solid';

const COUNTRIES = ['US', 'UK', 'CA', 'AU'];
const pct = (s) => Math.round((s || 0) * 100);
const controversyColor = (s) => (s >= 0.7 ? 'text-red-400' : s >= 0.4 ? 'text-amber-400' : 'text-slate-500');

function Metric({ label, value, Icon, accent }) {
  return (
    <div className="rounded-xl bg-slate-900 border border-slate-800 p-3">
      <Icon className={`w-4 h-4 ${accent} mb-1.5`} />
      <div className="text-2xl font-black text-white leading-none">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function ActionBtn({ onClick, label, busy, subtle }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className={`w-full py-2.5 rounded-lg font-bold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
        subtle ? 'bg-slate-800 text-slate-200 hover:bg-slate-700' : 'bg-blue-500 text-white hover:bg-blue-600'
      }`}
    >
      {busy && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
      {label}
    </button>
  );
}

export default function AdminDashboard({ isOpen, onClose, onRefreshComplete }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [stats, setStats] = useState({ live: 0, pending: 0, evergreen: 0, topicOfDay: null });
  const [queue, setQueue] = useState([]);
  const [country, setCountry] = useState('US');
  const [insights, setInsights] = useState(null);
  const [modCases, setModCases] = useState(null);
  const [apiKeys, setApiKeys] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [ingest, setIngest] = useState(null);

  const loadIngest = async () => {
    setBusy('ingest');
    try { setIngest(await getIngestionStatus()); } catch (e) { toast.error(e?.message || 'Failed'); }
    setBusy('');
  };

  const resetFeed = async () => {
    if (!window.confirm('Clear all auto-generated news topics that have no debate activity? (Topics with debates are kept.) This gives the feed a clean slate.')) return;
    setBusy('purge');
    try {
      const res = await purgeNewsFeed();
      toast.success(`Cleared ${res?.deleted || 0} topics (kept ${res?.kept || 0} with debates)`);
      await load();
    } catch (e) { toast.error(e?.message || 'Failed'); }
    setBusy('');
  };

  const loadApiKeys = async () => {
    setBusy('keys');
    try { setApiKeys(await listDataApiKeys()); } catch (e) { toast.error(e?.message || 'Failed'); }
    setBusy('');
  };
  const mintApiKey = async () => {
    const label = window.prompt('Partner/label for this API key?', 'partner');
    if (!label) return;
    setBusy('mintkey');
    try {
      const res = await createDataApiKey(label);
      setNewKey(res);
      toast.success('API key created');
      await loadApiKeys();
    } catch (e) { toast.error(e?.message || 'Failed'); }
    setBusy('');
  };
  const revokeKey = async (key) => {
    if (!window.confirm('Revoke this API key? The partner will lose access immediately.')) return;
    try { await revokeDataApiKey(key); toast.success('Revoked'); await loadApiKeys(); }
    catch (e) { toast.error(e?.message || 'Failed'); }
  };

  const loadModCases = async () => {
    setBusy('mod');
    try {
      setModCases(await getModerationCases('pending_review'));
    } catch (e) {
      toast.error(e?.message || 'Failed to load cases');
    }
    setBusy('');
  };

  const resolveCase = async (caseId, decision) => {
    setBusy('mod_' + caseId);
    try {
      await resolveModerationCase(caseId, decision);
      toast.success(`Case ${decision}ed`);
      setModCases((prev) => (prev || []).filter((c) => c.id !== caseId));
    } catch (e) {
      toast.error(e?.message || 'Failed to resolve');
    }
    setBusy('');
  };

  const loadInsights = async () => {
    setBusy('insights');
    try {
      const data = await getOpinionInsights();
      setInsights(data);
    } catch (e) {
      toast.error(e?.message || 'Failed to load insights');
    }
    setBusy('');
  };

  const exportInsightsCSV = () => {
    if (!insights) return;
    const rows = [['type', 'key', 'sideA', 'sideB', 'total', 'leanA_pct', 'topics']];
    [...(insights.categories || []), ...(insights.regions || [])].forEach((r) => {
      rows.push([r.type, r.key, r.sideA, r.sideB, r.total, r.leanAPct, r.topics]);
    });
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `verbi_opinion_insights_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const liveSnap = await getDocs(query(collection(db, 'topics'), where('status', '==', 'active')));
      const live = liveSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const evergreen = live.filter((t) => t.isEvergreen).length;
      const topicOfDay = live.find((t) => t.isTopicOfDay) || null;

      const pendSnap = await getDocs(query(collection(db, 'globalTopics'), where('status', '==', 'pending_approval')));
      const pend = pendSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.controversyScore || 0) - (a.controversyScore || 0));

      setStats({ live: live.length, pending: pend.length, evergreen, topicOfDay });
      setQueue(pend);
    } catch (e) {
      console.error('Admin load failed', e);
      toast.error('Failed to load admin data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) { load(); loadIngest(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, load]);

  const run = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(successMsg || res?.message || 'Done');
      await load();
      if (onRefreshComplete) onRefreshComplete();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Action failed');
    }
    setBusy('');
  };

  const notifyUpdate = async () => {
    if (!window.confirm("Send a push notification to ALL users that the app has been updated? This goes to every device — run it only after you've deployed a new version.")) return;
    setBusy('notify');
    try {
      const res = await broadcastUpdateNotification({});
      toast.success(`Update notification sent to ${res?.sent ?? 0} device(s).`);
    } catch (e) {
      toast.error(e?.message || 'Broadcast failed');
    }
    setBusy('');
  };

  const approve = async (topic) => {
    try {
      await approveGlobalTopic(topic);
      setQueue((q) => q.filter((t) => t.id !== topic.id));
      setStats((s) => ({ ...s, pending: Math.max(0, s.pending - 1), live: s.live + 1 }));
      toast.success('Published to feed');
      if (onRefreshComplete) onRefreshComplete();
    } catch (e) {
      toast.error('Approve failed');
    }
  };

  const reject = async (topic) => {
    try {
      await rejectGlobalTopic(topic.id);
      setQueue((q) => q.filter((t) => t.id !== topic.id));
      setStats((s) => ({ ...s, pending: Math.max(0, s.pending - 1) }));
    } catch (e) {
      toast.error('Reject failed');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-sm overflow-y-auto">
      <div className="max-w-4xl mx-auto p-4 md:p-6" style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
              <BoltIcon className="w-6 h-6 text-yellow-400" /> Newsroom Control
            </h1>
            <p className="text-xs text-slate-400 font-mono mt-0.5">Verbi editorial command center</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Metric label="Live Topics" value={stats.live} Icon={FireIcon} accent="text-orange-400" />
          <Metric label="In Review" value={stats.pending} Icon={NewspaperIcon} accent="text-amber-400" />
          <Metric label="Evergreen" value={stats.evergreen} Icon={SparklesIcon} accent="text-emerald-400" />
          <Metric label="Topic of Day" value={stats.topicOfDay ? '1' : '—'} Icon={StarIcon} accent="text-yellow-400" />
        </div>

        {/* Topic of the Day banner */}
        {stats.topicOfDay && (
          <div className="mb-6 rounded-xl bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/30 p-4">
            <div className="flex items-center gap-2 text-yellow-400 text-xs font-black uppercase tracking-wider mb-1">
              <StarIcon className="w-4 h-4" /> Today's House Battle
            </div>
            <p className="text-white font-bold text-sm">{stats.topicOfDay.title}</p>
          </div>
        )}

        {/* Actions */}
        <div className="mb-6">
          <h2 className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3">Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-4">
              <div className="flex items-center gap-2 text-white font-bold mb-3">
                <GlobeAmericasIcon className="w-5 h-5 text-blue-400" /> National News · 4 countries
              </div>
              <p className="text-[11px] text-slate-400 mb-3">Pulls the newest national headlines for US, Canada, UK &amp; Australia — deduped, classified, newest-first.</p>
              <ActionBtn
                busy={busy === 'seed'}
                onClick={() => run('seed', () => runNationalFeedUpdate({ limit: 12 }), 'National news pulled').then(loadIngest)}
                label="Pull National News (all)"
              />
              <button onClick={resetFeed} disabled={busy === 'purge'} className="w-full mt-2 py-2 rounded-lg text-xs font-bold bg-red-900/40 text-red-300 border border-red-500/30 hover:bg-red-900/60 disabled:opacity-50">{busy === 'purge' ? 'Clearing…' : 'Reset feed · clear unused news'}</button>
            </div>
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-4">
              <div className="flex items-center gap-2 text-white font-bold mb-3">
                <SparklesIcon className="w-5 h-5 text-emerald-400" /> Backbone & Battle
              </div>
              <div className="flex flex-col gap-2">
                <ActionBtn
                  busy={busy === 'evergreen'}
                  onClick={() => run('evergreen', () => generateEvergreenTopics({ count: 12 }), 'Evergreen backbone generated')}
                  label="Generate Evergreen (12)"
                  subtle
                />
                <ActionBtn
                  busy={busy === 'totd'}
                  onClick={() => run('totd', () => forceTopicOfDayCycle(), 'Topic of the Day cycled')}
                  label="Run Topic of the Day"
                  subtle
                />
              </div>
            </div>
          </div>

          {/* Release announcement — run after deploying a new version */}
          <div className="mt-3 rounded-xl bg-gradient-to-r from-indigo-900/40 to-purple-900/40 border border-indigo-500/30 p-4">
            <div className="flex items-center gap-2 text-white font-bold mb-1">
              <BellAlertIcon className="w-5 h-5 text-indigo-300" /> Announce a Release
            </div>
            <p className="text-[11px] text-slate-400 mb-3">
              Run this <span className="font-bold text-slate-300">after</span> deploying a new version — pushes an "app updated" notification to every user's phone.
            </p>
            <button
              onClick={notifyUpdate}
              disabled={busy === 'notify'}
              className="w-full py-2.5 rounded-lg font-bold text-sm bg-indigo-500 text-white hover:bg-indigo-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy === 'notify' && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
              Notify All Users of Update
            </button>
          </div>
        </div>

        {/* Ingestion diagnostics — is the controversy filter actually working? */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-500">Feed Ingestion · last run</h2>
            <button onClick={loadIngest} disabled={busy === 'ingest'} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50">{busy === 'ingest' ? '…' : 'Refresh'}</button>
          </div>
          {!ingest ? (
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-6 text-center text-slate-500 text-sm">Loading…</div>
          ) : (
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                <div className="text-center"><div className="text-xl font-black text-white">{ingest.lastRunFetched}</div><div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Fetched</div></div>
                <div className="text-center"><div className="text-xl font-black text-orange-400">{ingest.lastRunPublished}</div><div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Debates published</div></div>
                <div className="text-center"><div className="text-xl font-black text-slate-400">{ingest.lastRunNotDebatable || 0}</div><div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Not debatable</div></div>
                <div className="text-center"><div className="text-xl font-black text-red-400">{ingest.lastRunSkippedSensitive}</div><div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Held (sensitive)</div></div>
              </div>
              <div className="text-[11px] text-slate-500 mb-3">
                Last run: {ingest.lastRunAt ? new Date(ingest.lastRunAt).toLocaleString() : 'never'} ({ingest.lastRunPass || '—'} pass) · <span className="text-orange-400 font-bold">{ingest.debateWorthyTotal}</span> debate-worthy live now, {ingest.activeTotal} active total.
              </div>
              {ingest.lastRunFetched === 0 && (
                <div className="text-[12px] text-amber-400 bg-amber-950/30 border border-amber-500/30 rounded-lg p-2 mb-3">Last run fetched 0 articles (likely throttle-skipped or no new RSS). Hit "Seed National" above to force a fresh pull.</div>
              )}
              {ingest.samples && ingest.samples.length > 0 && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1.5">AI verdicts (last run) — the debate question it wrote</div>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {ingest.samples.map((s, i) => {
                      const ok = s.debatable ?? s.controversial;
                      return (
                        <div key={i} className="flex items-center gap-2 text-[12px]">
                          <span className={`shrink-0 w-4 text-center font-black ${s.sensitive ? 'text-red-400' : ok ? 'text-orange-400' : 'text-slate-600'}`}>{s.sensitive ? '⚠' : ok ? '✓' : '·'}</span>
                          {typeof s.polarization === 'number' && <span className={`shrink-0 w-8 text-right text-[10px] font-mono ${s.polarization >= 0.7 ? 'text-orange-400' : s.polarization >= 0.45 ? 'text-slate-400' : 'text-slate-600'}`}>{Math.round(s.polarization * 100)}</span>}
                          <span className="text-slate-300 truncate">{s.title}</span>
                          <span className="shrink-0 text-[9px] uppercase tracking-wider text-slate-600">{s.sensitive ? 'held' : ok ? 'published' : 'skipped'}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-slate-600 mt-2">✓ = published as a debate topic · · = skipped (not debatable) · ⚠ = held as sensitive</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* AI Moderation — cases where community flags crossed the threshold */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-500">Moderation · AI-reviewed cases</h2>
            <button onClick={loadModCases} disabled={busy === 'mod'} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50">{busy === 'mod' ? 'Loading…' : (modCases ? 'Refresh' : 'Load cases')}</button>
          </div>
          {!modCases ? (
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-6 text-center text-slate-500 text-sm">
              Posts flagged by more than 3 different accounts are auto-hidden, their points cancelled, and reviewed by AI. Decide here, or the AI's recommendation auto-applies after 24h.
            </div>
          ) : modCases.length === 0 ? (
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-6 text-center text-emerald-400/80 text-sm">All clear — no cases awaiting review.</div>
          ) : (
            <div className="space-y-3">
              {modCases.map((c) => {
                const rec = c.aiRecommendation;
                const recColor = rec === 'suspend' ? 'bg-red-500/15 text-red-400 border-red-500/30' : rec === 'warn' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : 'bg-slate-700/40 text-slate-300 border-slate-600';
                return (
                  <div key={c.id} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${recColor}`}>AI: {rec}</span>
                      <span className="text-[10px] font-bold text-slate-500 uppercase">Severity {c.aiSeverity} · {Math.round((c.aiConfidence || 0) * 100)}% conf</span>
                      <span className="text-[10px] font-bold text-red-400/80 uppercase ml-auto">{c.flagCount} flags</span>
                    </div>
                    <p className="text-sm text-slate-200 bg-slate-950/60 rounded-lg p-3 mb-2 line-clamp-4">“{c.postBody}”</p>
                    {c.aiSummary && <p className="text-[12px] text-slate-400 mb-3"><span className="font-bold text-slate-500 uppercase text-[10px] tracking-wider">AI summary:</span> {c.aiSummary}</p>}
                    <div className="flex gap-2">
                      <button onClick={() => resolveCase(c.id, 'dismiss')} disabled={busy === 'mod_' + c.id} className="flex-1 py-2 rounded-lg text-xs font-bold bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50">Dismiss · restore</button>
                      <button onClick={() => resolveCase(c.id, 'warn')} disabled={busy === 'mod_' + c.id} className="flex-1 py-2 rounded-lg text-xs font-bold bg-amber-600/80 text-white hover:bg-amber-600 disabled:opacity-50">Warn</button>
                      <button onClick={() => resolveCase(c.id, 'suspend')} disabled={busy === 'mod_' + c.id} className="flex-1 py-2 rounded-lg text-xs font-bold bg-red-600 text-white hover:bg-red-500 disabled:opacity-50">Suspend</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Opinion Insights — the aggregated, de-identified data asset */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-500">Opinion Insights · sellable data</h2>
            <div className="flex gap-2">
              <button onClick={() => run('agg', () => forceAggregateInsights(), 'Insights aggregated').then(loadInsights)} disabled={busy === 'agg'} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50">{busy === 'agg' ? 'Running…' : 'Run Aggregation'}</button>
              <button onClick={loadInsights} disabled={busy === 'insights'} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50">{busy === 'insights' ? 'Loading…' : 'View'}</button>
              {insights && <button onClick={exportInsightsCSV} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500">Export CSV</button>}
            </div>
          </div>
          {!insights ? (
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-6 text-center text-slate-500 text-sm">
              Aggregated, de-identified opinion splits by category and region — over time. Run the nightly aggregation (or "Run Aggregation" now), then "View" to see how each group leans. This is the data product you can license/sell.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[{ label: 'By Category', rows: insights.categories }, { label: 'By Region', rows: insights.regions }].map((grp) => (
                <div key={grp.label} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">{grp.label}</div>
                  {(!grp.rows || grp.rows.length === 0) ? (
                    <div className="text-slate-600 text-xs py-3">No data yet — needs debate participation.</div>
                  ) : (
                    <div className="space-y-2">
                      {grp.rows.slice(0, 10).map((r) => (
                        <div key={r.id} className="flex items-center gap-2">
                          <div className="w-20 shrink-0 text-[11px] font-bold text-slate-300 truncate">{r.key}</div>
                          <div className="flex-1 h-2.5 rounded-full bg-slate-800 overflow-hidden flex">
                            <div className="h-full bg-blue-500" style={{ width: `${r.leanAPct}%` }} />
                            <div className="h-full bg-red-500" style={{ width: `${100 - r.leanAPct}%` }} />
                          </div>
                          <div className="w-16 shrink-0 text-right text-[10px] font-mono text-slate-500">{r.leanAPct}% · {r.total}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Data API — external partners connect to the anonymized dataset */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-500">Data API · partner access</h2>
            <div className="flex gap-2">
              <button onClick={mintApiKey} disabled={busy === 'mintkey'} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50">{busy === 'mintkey' ? '…' : '+ New key'}</button>
              <button onClick={loadApiKeys} disabled={busy === 'keys'} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50">{busy === 'keys' ? '…' : 'Load'}</button>
            </div>
          </div>
          {newKey && (
            <div className="rounded-xl bg-emerald-950/40 border border-emerald-500/30 p-4 mb-3">
              <div className="text-[10px] font-black uppercase tracking-wider text-emerald-400 mb-1">New key — copy it now</div>
              <code className="block text-xs text-emerald-200 break-all mb-2">{newKey.apiKey}</code>
              <div className="text-[11px] text-slate-400">Endpoint: <code className="text-slate-300 break-all">{newKey.endpoint}?apikey={newKey.apiKey}</code></div>
            </div>
          )}
          {!apiKeys ? (
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-6 text-center text-slate-500 text-sm">
              Mint keys for companies to pull the anonymized, k-anonymized opinion dataset (categories, regions, and demographic cross-tabs). No personal data is ever exposed.
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-4 text-center text-slate-500 text-sm">No keys yet.</div>
          ) : (
            <div className="space-y-2">
              {apiKeys.map((k) => (
                <div key={k.id} className="rounded-xl bg-slate-900 border border-slate-800 p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-slate-200 truncate">{k.label} {k.active === false && <span className="text-red-400 text-[10px] uppercase ml-1">revoked</span>}</div>
                    <div className="text-[10px] font-mono text-slate-500 truncate">{k.id} · {k.calls || 0} calls</div>
                  </div>
                  {k.active !== false && <button onClick={() => revokeKey(k.id)} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-red-600/80 text-white hover:bg-red-600 shrink-0">Revoke</button>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Review queue */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-500">Review Queue · by controversy</h2>
            <button onClick={load} className="text-slate-400 hover:text-white">
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {queue.length === 0 && !loading && (
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-8 text-center text-slate-500 text-sm">
              Queue empty. Strong national topics auto-publish straight to the feed — only borderline, sensitive, or regional ones land here for review.
            </div>
          )}

          <div className="space-y-2">
            {queue.map((t) => (
              <div key={t.id} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                        {t.category || 'General'}
                      </span>
                      {t.sensitive && (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 flex items-center gap-0.5">
                          <ExclamationTriangleIcon className="w-3 h-3" /> Sensitive
                        </span>
                      )}
                      <span className={`text-[10px] font-mono font-bold ${controversyColor(t.controversyScore || 0)}`}>
                        ◆ {pct(t.controversyScore)}% controversy
                      </span>
                    </div>
                    <p className="text-white font-bold text-sm leading-snug">{t.title}</p>
                    <p className="text-slate-500 text-xs mt-0.5">
                      {t.sourceStation || 'News'} · {t.region?.stateCode || 'National'}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => approve(t)}
                      title="Publish to feed"
                      className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500 hover:text-white transition-colors"
                    >
                      <CheckIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => reject(t)}
                      title="Reject"
                      className="p-2 rounded-lg bg-slate-800 text-slate-500 hover:bg-red-500 hover:text-white transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
